import React from "react";
import { Helmet } from "react-helmet-async";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import mainBackground from "../../assets/images/background/main_background.png";
import "./ApplicationForTrainers.css";

// This page serves as a pillar page to describe Zasfit in detail.
// It explains what the platform does, who it is for, how it works and
// why it can help trainers grow their business. The sections are
// structured with headings to aid readability. Feel free to adapt
// and expand this content as the product evolves.

const CANONICAL =
  "https://zasfit.io/aplicacion-para-entrenadores-personales/";

export default function ApplicationForTrainers() {
  // Structured data for the SoftwareApplication entity.
  const softwareLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Zasfit",
    applicationCategory: "HealthApplication",
    operatingSystem: "iOS, Android, Web",
    inLanguage: "es-ES",
    url: CANONICAL,
    image: "https://zasfit.io/static/media/og.jpg",
    publisher: { "@type": "Organization", name: "Zasfit" },
    brand: { "@type": "Brand", name: "Zasfit" },
    offers: {
      "@type": "Offer",
      price: "24.99",
      priceCurrency: "EUR",
      description:
        "Plan Básico con rutinas, dietas y seguimiento. Otros planes disponibles con más clientes y funcionalidades."
    },
    potentialAction: {
      "@type": "RegisterAction",
      target: "https://app.zasfit.io/auth/register"
    }
  };

  // FAQ schema.
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "¿Qué es Zasfit?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Zasfit es una plataforma online que permite a entrenadores personales crear rutinas y dietas, hacer seguimiento de sus clientes y gestionar pagos desde un mismo lugar."
        }
      },
      {
        "@type": "Question",
        name: "¿Cómo funciona la prueba gratuita?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Puedes crear una cuenta y utilizar todas las funciones de Zasfit durante 15 días sin coste. Al finalizar, podrás elegir el plan que mejor se adapte a tus necesidades."
        }
      },
      {
        "@type": "Question",
        name: "¿Qué planes de suscripción existen?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Zasfit ofrece planes desde 24,99 € al mes con 10 clientes incluidos hasta planes con clientes ilimitados y soporte dedicado."
        }
      },
      {
        "@type": "Question",
        name: "¿Se pueden personalizar las rutinas y dietas?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Sí. Puedes crear ejercicios con tus propios vídeos, establecer macros y calorías y ajustar las rutinas y dietas de cada cliente en cualquier momento."
        }
      },
      {
        "@type": "Question",
        name: "¿Hay app para los clientes?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Zasfit incluye aplicaciones nativas para iOS y Android. Tus clientes podrán ver sus rutinas y dietas, registrar métricas y recibir notificaciones y mensajes."
        }
      }
    ]
  };

  // Breadcrumb schema.
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: "https://zasfit.io/" },
      { "@type": "ListItem", position: 2, name: "Aplicación para Entrenadores", item: CANONICAL }
    ]
  };

  return (
    <>
      {/* HERO con mismo fondo que la landing. No tocamos el CSS del Header. */}
      <section
        className="hero-wrapper app-hero text-white"
        style={{
          backgroundImage: `url(${mainBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }}
      >
        <Header />

        {/* Espaciado superior = altura de la cabecera para que NADA quede tapado */}
        <div className="hero-content container mt-50">
          <h1 className="hero-title">Aplicación para<br /> Entrenadores Personales</h1>
          <p className="hero-subtitle">
            Con Zasfit puedes centralizar todas las tareas de tu negocio:
            crear y asignar rutinas, diseñar planes de nutrición, hacer
            seguimiento de métricas y gestionar tus pagos en un solo lugar.
          </p>

        </div>
      </section>

      {/* CONTENIDO con fondo blanco */}
      <main className="container py-4">
        <Helmet>
          <title>Aplicación para entrenadores personales: guía y software todo-en-uno</title>
          <meta
            name="description"
            content="Zasfit es una aplicación para entrenadores personales que combina creación de rutinas, dietas y seguimiento en una sola plataforma. Descubre cómo funciona y cómo puede ayudarte a escalar tu negocio."
          />
          <link rel="canonical" href={CANONICAL} />
          <script type="application/ld+json">{JSON.stringify(softwareLd)}</script>
          <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
          <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        </Helmet>

        {/* (FAQs visibles se mantienen más abajo) */}

        <section className="my-4">
          <h2>¿Qué es Zasfit?</h2>
          <p>
            Zasfit es un software de gestión para entrenadores que integra en una sola plataforma
            las funciones de programación de entrenamientos, nutrición, seguimiento del progreso y
            gestión de clientes. Permite crear ejercicios con vídeos propios o seleccionar de su
            biblioteca, diseñar dietas personalizadas con calorías y macros, y analizar la evolución
            de cada persona mediante gráficos y métricas. Incluye aplicaciones para iOS y Android de
            modo que tus clientes siempre tengan a mano sus rutinas y puedan registrar feedback de
            forma sencilla.
          </p>
        </section>

        <section className="my-4">
          <h2>Características destacadas</h2>
          <ul>
            <li>
              <strong>Rutinas ilimitadas:</strong> Crea programas con tus ejercicios o usa la
              biblioteca de Zasfit. Duplica días o semanas para acelerar tu flujo de trabajo y
              asigna rutinas visualmente con arrastrar y soltar.
            </li>
            <li>
              <strong>Planificación de dietas:</strong> Diseña planes semanales u opciones de
              comidas con cálculos automáticos de calorías y macros. Ajusta cantidades y asigna la
              dieta al instante.
            </li>
            <li>
              <strong>Seguimiento del progreso:</strong> Peso, grasa, medidas, adherencia y
              evolución por ejercicio. Recibe alertas cuando un cliente se estanca.
            </li>
            <li>
              <strong>App para clientes:</strong> Consultan rutinas y dietas, registran resultados
              y reciben recordatorios. Mensajería integrada.
            </li>
            <li>
              <strong>Gestión de pagos:</strong> Facturación integrada (tarjetas y SEPA) con
              notificaciones al cliente.
            </li>
            <li>
              <strong>Mensajería y recordatorios:</strong> Chat, mensajes automáticos y soporte
              dentro del panel.
            </li>
          </ul>
        </section>

        <section className="my-4">
          <h2>Beneficios para entrenadores</h2>
          <p>
            Zasfit está diseñada para optimizar tu tiempo. Con duplicados y automatizaciones,
            puedes crear semanas de entrenamiento en minutos y reutilizar dietas para objetivos
            similares. Los paneles de métricas te permiten ajustar sobre la marcha y anticipar
            renovaciones e ingresos.
          </p>
        </section>

        <section className="my-4">
          <h2>Casos de uso</h2>
          <ul>
            <li><strong>Uno a uno:</strong> Planes personalizados y feedback individual.</li>
            <li><strong>Modelo híbrido:</strong> Combina presencial y online para escalar.</li>
            <li><strong>Grupos y retos:</strong> Hábitos, seguimiento y analíticas agregadas.</li>
            <li><strong>Gimnasios/estudios:</strong> Varios entrenadores, permisos y plantillas.</li>
          </ul>
        </section>

        <section className="my-4">
          <h2>Cómo funciona</h2>
          <ol>
            <li>Regístrate, configura tu perfil y horarios/servicios.</li>
            <li>Crea rutinas y dietas o importa tus plantillas.</li>
            <li>Invita a clientes por email: acceso inmediato a la app.</li>
            <li>Haz seguimiento y ajusta con ayuda de gráficos y alertas.</li>
          </ol>
        </section>

        <section className="my-4">
          <h2>Integraciones y soporte</h2>
          <p>
            Integra Stripe y GoCardless para pagos. Soporte por chat en vivo y base de conocimiento
            en español. Próximamente, integraciones con CRM y marketing.
          </p>
        </section>

        <section className="my-4">
          <h2>Planes y precios</h2>
          <ul>
            <li><strong>Básico (24,99 €/mes):</strong> Hasta 10 clientes.</li>
            <li><strong>Avanzado (39,99 €/mes):</strong> Hasta 20 clientes.</li>
            <li><strong>Profesional (79,99 €/mes):</strong> Hasta 50 clientes.</li>
            <li><strong>Premium (149,99 €/mes):</strong> Hasta 100 clientes.</li>
            <li><strong>Ultimate (249,99 €/mes):</strong> Clientes ilimitados.</li>
          </ul>
          <p>Todos los planes incluyen 15 días de prueba gratuitos.</p>
        </section>

        <section className="my-4">
          <h2>Testimonios</h2>
          <blockquote className="quote">
            “Con Zasfit tardo la mitad del tiempo en preparar semanas completas y mis clientes
            valoran la app por su claridad y recordatorios.”
            <cite> — Laura G., entrenadora personal</cite>
          </blockquote>
          <blockquote className="quote mt-3">
            “Antes usaba hojas de cálculo y apps separadas; ahora con Zasfit gestiono todo en un solo
            panel y mis clientes pueden seguir su progreso fácilmente.”
            <cite> — David P., preparador físico</cite>
          </blockquote>
        </section>

        {/* ✅ FAQs visibles (no se quitan) */}
        <section className="my-4">
          <h2>Preguntas frecuentes</h2>
          <details>
            <summary>¿Qué es Zasfit?</summary>
            <p>Zasfit es una plataforma integral para entrenadores que reúne programación,
            nutrición, seguimiento y pagos en un solo lugar.</p>
          </details>
          <details>
            <summary>¿Cómo funciona la prueba gratuita?</summary>
            <p>Crea tu cuenta y disfruta de todas las funciones durante 15 días sin coste. Si te
            convence, podrás elegir el plan que mejor se ajuste a tu volumen de clientes.</p>
          </details>
          <details>
            <summary>¿Se puede personalizar la app con mi marca?</summary>
            <p>Los planes Premium y Ultimate ofrecen la posibilidad de personalizar la aplicación con
            tu logotipo, colores y dominio.</p>
          </details>
          <details>
            <summary>¿Puedo importar mis propios ejercicios y dietas?</summary>
            <p>Sí. Puedes subir vídeos e imágenes de tus propios ejercicios y crear planes de
            alimentación desde cero o duplicar plantillas existentes.</p>
          </details>
          <details>
            <summary>¿Qué opciones de soporte hay?</summary>
            <p>Todos los planes incluyen soporte por correo. Los planes Profesionales y superiores
            también disponen de chat en vivo y llamadas programadas con el equipo de éxito de
            clientes.</p>
          </details>
        </section>

        <section className="call-to-action my-5 text-center">
          <a
            className="theme-btn style-five btn-start btn-start-free-trial"
            href="https://app.zasfit.io/auth/register"
          >
            Prueba Gratis 7 Días
          </a>

          <p className="mt-3">
            También puedes visitar la{" "}
            <a href="https://zasfit.io" className="internal-link">
              página principal de la aplicación para entrenadores personales Zasfit
            </a>
            , donde encontrarás un resumen visual de la plataforma y sus planes.
          </p>
        </section>

      </main>

      <Footer />
    </>
  );
}
